// Final CreateWorkflowPage - Actions logic also moved to component

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { FileText, ArrowLeft, Eye, Edit } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Stepper } from "@shared/components/Stepper";
import {
  useUpdateWorkflowTrigger,
  useUpdateWorkflowActions,
  useWorkflow,
} from "../../hooks";

import { useToast } from "@shared/hooks/use-toast";
import {
  WorkflowFormData,
  DEFAULT_WORKFLOW_FORM_DATA,
  UpdateWorkflowActionsRequest,
} from "../../types";

import { transformUIActionsToAPI } from "../../utils";
import WorkflowDetailsStep from "./steps/Details";
import WorkflowTriggerStep from "./steps/Trigger";
import WorkflowActionsStep from "./steps/Actions";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

const CreateWorkflowPage = () => {
  const [formData, setFormData] = useState<WorkflowFormData>({
    ...DEFAULT_WORKFLOW_FORM_DATA,
  });
  const startTimeRef = useRef<number | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const utilityId = useRemoteUtilityId();

  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || "create";
  const workflowId = queryParams.get("id") || "";

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  // Remove mutation hooks since they're now in the components
  const updateActionsMutation = useUpdateWorkflowActions();

  const { data: existingWorkflow } = useWorkflow(
    workflowId ? parseInt(workflowId) : undefined
  );

  // SIMPLIFIED handleStepSave - Only updates formData, all API logic is in components
  const handleStepSave = useCallback(
    async (stepIndex: number, stepData: any) => {
      try {
        // Just update formData for state management - all API calls are in components
        setFormData((prev) => ({
          ...prev,
          [getStepDataKey(stepIndex)]: stepData,
          workflowId: stepData?.workflowId || prev.workflowId,
        }));

        return { success: true, updatedStepData: stepData };
      } catch (error: any) {
        return { success: false, error: error.message };
      }
    },
    []
  );

  const getStepDataKey = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return "details";
      case 1:
        return "trigger";
      case 2:
        return "actions";
      default:
        return "details";
    }
  };

  // SIMPLIFIED handleComplete - Just navigation, all logic is in components
  const handleComplete = useCallback(
    async (allStepData: any) => {
      // All validation and API calls are handled in individual step components
      // Just show success message and navigate

      const workflowName =
        allStepData[0]?.name || allStepData.details?.name || "Workflow";

      toast({
        title: isEditMode
          ? "Workflow Updated Successfully!"
          : "Workflow Created Successfully!",
        description: `"${workflowName}" is now ${
          isEditMode ? "updated and" : ""
        } active and ready to use.`,
      });

      // Clean up localStorage
      try {
        localStorage.removeItem("workflow-builder-progress");
        if (workflowId) {
          localStorage.removeItem(`workflow-builder-progress-${workflowId}`);
        }
      } catch (storageError) {
        toast({
          title: "",
          description: storageError?.message,
          variant: "destructive",
        });
      }

      // Navigate after a short delay
      setTimeout(() => {
        navigate("/communication-hub/workflows");
      }, 1500);

      return true;
    },
    [isEditMode, workflowId, toast, navigate]
  );

  const handleStepChange = useCallback(
    (stepIndex: number, previousStepIndex: number, stepInfo: any) => {
      // Optional: Add any step change logic here
    },
    []
  );

  // Steps configuration with all logic moved to components
  const steps = useMemo(
    () => [
      {
        title: "Workflow Details",
        slug: "details",
        component: (
          <WorkflowDetailsStep
            readOnly={isViewMode}
            existingWorkflow={existingWorkflow}
            isEditMode={isEditMode}
            mode={mode}
            workflowId={workflowId}
          />
        ),
        optional: false,
        infoTitle: "Workflow Details Help",
        infoDescription:
          "Enter the basic information for your workflow including name, description, and target audience.",
      },
      {
        title: "Trigger Configuration",
        slug: "trigger",
        component: (
          <WorkflowTriggerStep
            readOnly={isViewMode}
            existingWorkflow={existingWorkflow}
            isEditMode={isEditMode}
            createdWorkOrderID={parseInt(workflowId)}
          />
        ),
        optional: false,
        infoTitle: "Trigger Configuration Help",
        infoDescription:
          "Configure when this workflow should be triggered - either at a specific time or based on events.",
      },
      {
        title: "Actions Setup",
        slug: "actions",
        component: (
          <WorkflowActionsStep
            readOnly={isViewMode}
            existingWorkflow={existingWorkflow}
            isEditMode={isEditMode}
          />
        ),
        optional: false,
        infoTitle: "Actions Setup Help",
        infoDescription:
          "Add communication actions that will be executed when this workflow is triggered.",
      },
    ],
    [isViewMode, existingWorkflow, isEditMode, mode, workflowId]
  );

  // Pre-populate data for edit/view modes
  useEffect(() => {
    // Only update formData state, don't manipulate localStorage
    if (existingWorkflow?.id) {
      setFormData((prev) => ({
        ...prev,
        workflowId: existingWorkflow.id,
      }));
    }
  }, [existingWorkflow?.id]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="mr-2" />
            Workflow Builder
            {isViewMode && (
              <Badge
                variant="outline"
                className="ml-2 bg-blue-50 text-blue-700 flex items-center gap-1"
              >
                <Eye className="h-3 w-3" /> Viewing Workflow: #{workflowId}
              </Badge>
            )}
            {isEditMode && (
              <Badge
                variant="outline"
                className="ml-2 bg-amber-50 text-amber-700 flex items-center gap-1"
              >
                <Edit className="h-3 w-3" /> Editing Workflow: #{workflowId}
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">
            {isViewMode
              ? "View details of this automated workflow"
              : isEditMode
              ? "Edit this automated workflow"
              : "Create and configure a new automated workflow"}
          </p>
        </div>
      </div>

      {/* Stepper Component - Now purely for navigation and orchestration */}
      <Stepper
        steps={steps}
        onStepChange={handleStepChange}
        onComplete={handleComplete}
        onStepSave={handleStepSave}
        enableApiSave={true}
        saveOnStepChange={true}
        trackDirtyState={false}
        autoSave={true}
        persistProgress={true}
        allowSkip={false}
        storageKey={
          workflowId && isEditMode
            ? `workflow-builder-progress-${workflowId}`
            : "workflow-builder-progress"
        }
        enableRouting={true}
        onValidation={undefined}
        showDraftResume={true}
      />
    </div>
  );
};

export default CreateWorkflowPage;
